<template>
    <div>
        <div class="row gutter-sm" v-show="test_status === 'not_started'">
            <div class="col-12">
                <div class="row gutter-sm">
                    <div class="col-12">
                        {{ $t(getHelper) }}
                    </div>

                    <div class="col-12 text-weight-medium text-negative">
                        {{ $t('helper_general_test') }}
                    </div>

                    <div class="col-12">
                        {{ $t('questions') }}: <span class="text-weight-medium">{{ activity.qty_questions }}</span><br>
                        {{ $t('minimum_score') }}: <span class="text-weight-medium">{{ activity.minimum_score }}</span><br>
                        {{ $t('time_to_finish') }}: <span class="text-weight-medium">{{ activity.time_to_answer }}</span>
                    </div>
                </div>

                <div class="col-12 q-my-md" v-if="realizations.length > 0">
                    <q-list dense>
                        <q-collapsible icon="fas fa-pen" :label="$t('previous_results')">
                            <q-table
                                ref="table_realizations"
                                :pagination.sync="server_pagination"
                                :data="realizations"
                                :columns="columns"
                                row-key="id"
                                :no-data-label="$t('no_data_avaliable')"
                                :no-results-label="$t('no_results_found')"
                                :loading-label="$t('loading_data')"
                                :rows-per-page-label="$t('items_per_page')"
                                :rows-per-page-options="[0]"
                                hide-bottom
                                class="shadow-0"
                                >

                                <q-tr slot="header" slot-scope="props" :props="props">
                                    <q-th v-for="col in props.cols" :key="col.name" :props="props">
                                    {{ $t(col.label) }}
                                    </q-th>
                                </q-tr>

                                <template slot="body" slot-scope="props">
                                    <q-tr :props="props" @click.native="showModalResult(props.row)">
                                        <q-td key="date" :props="props">
                                            {{ fDate(props.row.date) }}
                                        </q-td>

                                        <q-td key="percent" :props="props">
                                            {{ props.row.percent.toFixed(2) }}%
                                        </q-td>

                                        <q-td key="score" :props="props">
                                            {{ props.row.score.toFixed(2) }}
                                        </q-td>

                                        <q-td key="test_score" :props="props">
                                            {{ props.row.test_score > 0 ? props.row.test_score.toFixed(2) : '' }}
                                        </q-td>

                                        <q-td key="status" :props="props">
                                            <span :class="getResultClass(props.row.status)">{{ $t(getResult(props.row.status)) }}</span>
                                        </q-td>
                                    </q-tr>
                                </template>
                            </q-table>
                        </q-collapsible>
                    </q-list>
                </div>

                <div class="row gutter-sm justify-end" v-if="activity.allowed_to_answer">
                    <div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
                        <q-btn color="tertiary" class="full-width" :label="$t('close')" icon="fas fa-ban" @click="closeTest()" />
                    </div>

                    <div class="col-xs-12 col-sm-3" :class="getOrder('save')">
                        <q-btn color="secondary" class="full-width" :label="$t('start')" icon="fas fa-play" @click="startTest()" />
                    </div>
                </div>

                <div class="row gutter-sm justify-end" v-if="!activity.allowed_to_answer">
                    <div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
                        <q-btn color="tertiary" class="full-width" :label="$t('close')" icon="fas fa-ban" @click="closeTest()" />
                    </div>
                </div>
            </div>
        </div>

        <div class="row" v-show="test_status === 'started'">
            <div class="col-12">
                <div class="row gutter-sm justify-end">
                    <countdown v-if="countdown" :time="time_to_answer" :emit-events="true" @end="countdownEnded()" @progress="handleProgress" :transform="transformValues">
                        <template slot-scope="props">
                            <span class="q-headline" :class="warning_class">
                                {{ $t('question') }} {{ current_question + 1 }} {{ $t('of') }} {{ questions.length }} -

                                <span :class="warning_class">
                                    {{ $t('time_left') }}: {{ props.hours }}:{{ props.minutes }}:{{ props.seconds }}
                                </span>
                            </span>
                        </template>
                    </countdown>
                </div>
            </div>

            <div class="col-12">
                <div class="row gutter-sm" v-if="questions.length > 0">
                    <div class="col-12">
                        <span :class="questions[current_question].error ? 'text-negative' : ''">
                            {{ current_question + 1 }}) <span v-html="questions[current_question].title"></span>
                        </span>

                        <q-field :error="questions[current_question].error" :error-label="$t('select_one_option')">
                            <q-option-group
                                type="radio"
                                color="secondary"
                                v-model="questions[current_question].answer"
                                :options="questions[current_question].answers"
                                class="questions q-mt-xs"
                                @input="questions[current_question].error = false"
                            />
                        </q-field>
                    </div>
                </div>

                <div class="row gutter-sm justify-end">
                    <div class="col-xs-12 col-sm-3">
                        <q-btn
                            color="secondary" class="full-width" @click="saveAnswer()"
                            :label="$t(current_question + 1 < questions.length ? 'next_question' : 'finish_test')"
                            :icon="$t(current_question + 1 < questions.length ? 'fas fa-chevron-right' : 'fas fa-check')"
                         />
                    </div>
                </div>
            </div>
        </div>

        <div class="row gutter-sm" v-show="test_status === 'finished'">
            <div class="col-12">
                <p class="q-title">{{ $t('test_finished') }}</p>
                <p>{{ $t('you_finished_the_test_and_your_result_is') }}:
                    <span :class="getResultClass(result.status)"><strong>{{ $t(getResult(result.status)) }}</strong></span>
                </p>
                <p v-if="result.status != 6">
                    {{ `${$t('your_score_was')} ${result.score} ${$t('of')} ${result.qty_questions}, ${$t('a_total_of')} ${result.percent}% ${$t('of_right_answers')}.` }}
                </p>
            </div>

            <div class="col-12">
                <div class="row gutter-sm justify-end">
                    <div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
                        <q-btn color="tertiary" class="full-width" :label="$t('close')" icon="fas fa-ban" @click="closeTest()" />
                    </div>
                </div>
            </div>
        </div>

        <q-modal v-model="show_modal" :content-css="{minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '90vh'}">
            <q-modal-layout>
                <q-toolbar slot="header">
                    <q-btn flat round dense @click="show_modal = false" icon="fas fa-times" />
                    <q-toolbar-title>
                        {{ $t('result') }}: {{ activity.type_title }}
                    </q-toolbar-title>
                </q-toolbar>

                <div class="layout-padding">
                    <div class="row gutter-sm justify-end question-list">
                        <div class="col-xs-12 col-sm-12" v-for="(question, index) in realization_questions" :key="index">
                            <p class="q-subheading text-weight-medium q-mb-xs" :class="question.is_correct ? 'text-positive' : 'text-negative'" :id="'qt' + question.id">
                                {{ index + 1 }}) <span v-html="question.title"></span>
                            </p>

                            <q-field :error="question.error">
                                <q-option-group
                                    type="radio"
                                    v-model="question.answer"
                                    :options="question.answers"
                                    class="questions q-mt-xs"
                                    :class="question.is_correct? 'checkbox-hit' : 'checkbox-error'"
                                    @input="question.error = false"
                                    readonly
                                />
                            </q-field>
                        </div>
                    </div>
                </div>
            </q-modal-layout>
        </q-modal>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'
const { formatDate } = date

export default {
    props: ['activity'],

    data() {
        return {
            test_status: 'not_started',
            questions: [],
            current_question: null,
            time_to_answer: 0,
            countdown: false,
            warning_class: '',
            result: {},
            realizations: [],
            server_pagination: {
                page: 1,
                rowsNumber: 10,
                rowsPerPage: 0,
                descending: false,
                sortBy: 'order'
            },
            columns: [
                {
                    name: 'date',
                    required: true,
                    label: 'date',
                    align: 'left',
                    field: 'date',
                    sortable: false,
                },
                {
                    name: 'percent',
                    required: true,
                    label: 'percent',
                    align: 'left',
                    field: 'percent',
                    sortable: false,
                },
                {
                    name: 'score',
                    required: true,
                    label: 'hits',
                    align: 'left',
                    field: 'score',
                    sortable: false,
                },
                {
                    name: 'test_score',
                    required: true,
                    label: 'score',
                    align: 'left',
                    field: 'test_score',
                    sortable: false,
                },
                {
                    name: 'status',
                    required: true,
                    label: 'status',
                    align: 'left',
                    field: 'status',
                    sortable: false,
                }
            ],
            realization_questions: [],
            show_modal: false
        }
    },

    computed: {
        ...mapGetters({
            access_token: 'common/access_token',
            clients: 'params/clients',
            id_user_client_selected: 'common/id_user_client_selected',
            id_user_language_selected: 'common/id_user_language_selected'
        }),

        getHeader() {
            let headers = {}
            return headers = {
                headers: {
                    'content-type': 'application/json',
                    'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
                    'authorization': 'Bearer ' + this.access_token
                }
            }
        },

        getHelper() {
            if (this.activity.type_slug === 'block_general_test') return 'helper_block_general_test'
            else if (this.activity.type_slug === 'restart_general_test') return 'helper_restart_general_test'
            else return ''
        },

        getMaxWidth() {
            if (this.$q.platform.is.mobile) return '100vw'
            else return '90vw'
        },

        getMinWidth() {
            if (!this.$q.platform.is.mobile) return '90vw'
        },
    },

    methods: {
        startTest() {
            this.$q.loading.show()

            this.$axios.get(`/etraining_courses/group_training/${this.$route.params.id}/group_training_test/${this.activity.type_id}/`, this.getHeader)
            .then(response => {
                this.questions = response.data.group_training_test_questions.map(q => {
                    return {
                        ...q,
                        answer: '',
                        error: false,
                        answers: q.group_training_test_question_answers.map(a => { return { label: a.title, value: a.id } })
                    }
                })

                let t = response.data.time_to_answer
                this.time_to_answer = Number(t.split(':')[0]) * 60 * 60 * 1000 + Number(t.split(':')[1]) * 60 * 1000 + Number(t.split(':')[2] * 100)

                this.current_question = 0
                this.test_status = 'started'
                this.countdown = true
            })
            .catch(e => {
                console.log(e)
            })
            .finally(() => {
                this.$q.loading.hide()
            })
        },

        saveAnswer() {
            if (this.questions[this.current_question].answer == '') {
                this.questions[this.current_question].error = true
            }
            else {
                this.$q.loading.show()

                let fd = new FormData()

                fd.append('question_id', this.questions[this.current_question].id)
                fd.append('answer_id', this.questions[this.current_question].answer)

                this.$axios.post(`/etraining_courses/group_training/${this.$route.params.id}/group_training_test/${this.activity.type_id}/submit_question/`, fd, this.getHeader)
                .then(response => {
                    if (!response.data.id) this.current_question++
                    else {
                        this.result = response.data
                        this.finishTest()
                    }
                })
                .catch(e => {
                    console.log(e)
                })
                .finally(() => {
                    this.$q.loading.hide()
                })
            }
        },

        finishTest() {
            this.countdown = false
            this.test_status = 'finished'
        },

        countdownEnded() {
            this.$q.loading.show()

            this.$axios.get(`/etraining_courses/group_training/${this.$route.params.id}/group_training_test/${this.activity.type_id}/set_group_training_test_user_expired/`, this.getHeader)
            .then(response => {
                this.result = response.data
                this.finishTest()
            })
            .catch(e => {
                console.log(e)
            })
            .finally(() => {
                this.$q.loading.hide()
            })
        },

        showModalResult(result) {
            this.$q.loading.show()
            this.realization_questions = []

            this.$axios.get(`/etraining_courses/group_training/${this.$route.params.id}/group_training_test/${this.activity.type_id}/group_training_test_user_result/${result.id}/`, this.getHeader)
            .then(response => {
                this.realization_questions = response.data.questions.map(q => {
                    return {
                        ...q,
                        answer: q.user_answer_id,
                        answers: q.group_training_test_question_answers.map(a => { return { label: a.title, value: a.id } })
                    }
                })

                this.show_modal = true
            })
            .catch(e => {
                console.log(e)
            })
            .finally(() => {
                this.$q.loading.hide()
            })
        },

        transformValues(props) {
            Object.entries(props).forEach(([key, value]) => {
                const digits = value < 10 ? `0${value}` : value
                props[key] = `${digits}`
            })

            return props
        },

        handleProgress(data) {
            if (data.totalMilliseconds / 1000 <= 300) this.warning_class = 'text-negative'
        },

        closeTest() {
            this.test_started = false
            this.$emit('closeTest')
        },

        getResult(status) {
            switch(status) {
                case 1: return 'aproved'
                break
                case 2: return 'reproved'
                break
                case 3: return 'blocked'
                break
                case 4: return 'restarted_training'
                break
                case 50: return 'improved_score'
                break
                case 60: return 'not_improved_score'
                break
                case 6: return 'time_expired'
                break
            }
        },

        getResultClass(status) {
            switch(status) {
                case 1:
                case 50: return 'text-positive'
                break
                case 2:
                case 3:
                case 6: return 'text-negative'
                break
                case 4: return 'text-warning'
                break
                case 60: return ''
                break
            }
        },

        fDate(d) {
            if (d) {
                let format = ''

                switch(this.id_user_language_selected) {
                    case 1:
                    case 3: format = 'DD/MM/YYYY HH:mm:ss'
                    break
                    default: format = 'MM-DD-YYYY HH:mm:ss'
                    break
                }

                return date.formatDate(d, format)
            }
        },

        getOrder(name) {
            if (name == 'cancel') {
                if (this.$q.platform.is.mobile) return 'order-last'
                else return 'order-first'
            }
            else if (name == 'save') {
                if (this.$q.platform.is.mobile) return 'order-first'
                else return 'order-last'
            }
        },
    },

    created() {
        this.realizations = this.activity.user_test_results.map(e => { return {...e} })
    }
}
</script>

<style>

</style>
